/**
 * Order Entry Page
 * Barcode scanner with offline order queue support
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BarcodeScanner from '../components/BarcodeScanner';
import ProductCaptureRow from '../components/ProductCaptureRow';
import { useProductCache } from '../hooks/useProductCache';
import { writeOps } from '../sync/writeOps';
import { apiFetch } from '../services/api';
import { CachedProduct, PendingOrderItem } from '../utils/indexedDB';
import { stepQty, validateOrder, isOrderingNotConfiguredError } from '../utils/orderRules';

interface CurrentOrderItem extends PendingOrderItem {
  isNew?: boolean;
  isUnknown?: boolean; // Flag for products not found in catalog
  // #2022 Phase 3 — captured qty-on-hand (inventory) + the product's order multiple.
  quantityOnHand?: number;
  order_increment?: number;
  // #2022 Phase 4 — captured via an OOS shelf-tag scan (records qtyonhand=0).
  isOOS?: boolean;
}

const OrderEntry: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get store context from URL params
  const storeId = parseInt(searchParams.get('storeId') || '0');
  const storeName = searchParams.get('storeName') || 'Unknown Store';
  const projectId = parseInt(searchParams.get('projectId') || '0');
  const callId = searchParams.get('callId') ? parseInt(searchParams.get('callId')!) : undefined;
  // Visit localId handed off by StoreDetail (#2013). Lets the queued order
  // resolve the server callid lazily when the check-in hasn't synced yet.
  const visitId = searchParams.get('visitId') || undefined;

  // #2022 Phase 3 — per-project workflow + order rules, handed off by StoreDetail.
  // requiresOrder defaults to true so legacy links (and the unconfigured-project
  // fallback) keep showing order entry. min/max are null when the distributor
  // leaves them unset (no client constraint; server still authoritative).
  const requiresOrder = (searchParams.get('requiresOrder') ?? '1') !== '0';
  const requiresInventory = searchParams.get('requiresInventory') === '1';
  const requiresOos = searchParams.get('requiresOos') === '1';
  const minOrderAmount = searchParams.get('minOrder') ? parseFloat(searchParams.get('minOrder')!) : null;
  const maxProductCount = searchParams.get('maxProduct') ? parseInt(searchParams.get('maxProduct')!) : null;
  const inventoryOnly = requiresInventory && !requiresOrder && !requiresOos;
  const oosOnly = requiresOos && !requiresOrder && !requiresInventory;
  // #3515 — the rep said which capture they came for by which button they tapped.
  // Honour it. Inferring the mode is what made a project requiring BOTH an order
  // and out-of-stocks open in ordering, titled "Order Entry", with outs behind a
  // toggle. Absent (deep link, older session) falls back to the old inference.
  const requestedMode = searchParams.get('mode');

  // State
  const [isScannerActive, setIsScannerActive] = useState(true);
  const [currentItems, setCurrentItems] = useState<CurrentOrderItem[]>([]);
  const [scannedProduct, setScannedProduct] = useState<CachedProduct | null>(null);
  const [quantity, setQuantity] = useState(1);
  // #2022 — on-hand value being captured in the add modal (inventory projects).
  const [modalOnHand, setModalOnHand] = useState('');
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [manualUPC, setManualUPC] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false); // #2341 brief post-save confirmation before auto-return
  const [showProductList, setShowProductList] = useState(false);
  // #2022 Phase 4 — explicit capture mode. 'capture' = scan product UPCs for
  // order/inventory; 'oos' = scan shelf tags (CodeNbr) to mark out-of-stock.
  // The active mode decides how each scan is interpreted, so a stray UPC can
  // never be silently mis-recorded as OOS. OOS-only projects start in 'oos'.
  const [mode, setMode] = useState<'capture' | 'oos'>(
    requestedMode === 'oos' ? 'oos' : requestedMode ? 'capture' : oosOnly ? 'oos' : 'capture',
  );

  // Unknown UPC handling state
  const [showUnknownUpcModal, setShowUnknownUpcModal] = useState(false);
  const [unknownUPC, setUnknownUPC] = useState('');
  const [unknownDescription, setUnknownDescription] = useState('');
  const [unknownCost, setUnknownCost] = useState('');

  // #2188 (diag #54) — set when this project requires an order but has no
  // distributor linked, so the product catalog can't load. We surface a clear
  // message instead of silently logging and falling back to the generic catalog.
  const [orderingConfigError, setOrderingConfigError] = useState<string | null>(null);

  // Hooks
  const {
    isLoading: _isCaching,
    cachedCount,
    cacheProductsForStore,
    lookupByUPC,
    lookupByCodeNbr,
    getProductsForStore,
  } = useProductCache();

  // Get the effective associate id the rest of the app uses
  // (`associate` in localStorage — refreshed on login and by the impersonation
  // set/clear paths). The page previously read from `rdm_user`, which is
  // never populated, so `associateId` defaulted to 0 and every saved order
  // got rejected server-side. Order still landed in IDB with status=failed
  // but the UI showed "Order saved successfully!" — silent data loss.
  // (#1471 follow-up #11)
  const associateStr = localStorage.getItem('associate');
  const associate = associateStr ? JSON.parse(associateStr) : null;
  const associateId = associate?.associateid ?? associate?.AssociateID ?? 0;

  // Calculate totals
  const totalItems = currentItems.reduce((sum, item) => sum + item.orderqty, 0);
  const totalCost = currentItems.reduce((sum, item) => sum + item.cost * item.orderqty, 0);

  // #2022 Phase 3 — hard-enforce the order rules client-side so the rep can't
  // build an order the server would 400 (which today surfaces as a silent
  // "Order saved" failure). Inventory-only submissions are never blocked.
  const validation = useMemo(
    () =>
      validateOrder(
        currentItems.map((i) => ({
          productid: i.productid,
          orderqty: i.orderqty,
          cost: i.cost,
          order_increment: i.order_increment,
        })),
        { requiresOrder, minOrderAmount, maxProductCount }
      ),
    [currentItems, requiresOrder, minOrderAmount, maxProductCount]
  );
  const overMaxIds = new Set(validation.overMaxProductIds);

  // Cache products on mount if we have valid store/project
  useEffect(() => {
    if (storeId && projectId) {
      setOrderingConfigError(null);
      cacheProductsForStore(storeId, projectId).catch((err) => {
        // A project that requires an order but has no distributor linked returns
        // a hard 400 ("Project does not have a retailer or distributor", diag
        // #54). That's a setup problem, not a transient/scan error — show a
        // clear message and steer the rep away from the generic catalog (which
        // would build an order against products that aren't this project's).
        if (isOrderingNotConfiguredError(err)) {
          setOrderingConfigError(
            'Ordering isn’t set up for this project yet — no distributor is linked, so there are no ' +
              'products to order. Please ask your admin to assign a distributor. You can still finish the ' +
              'rest of this task.'
          );
        } else {
          console.error(err);
        }
      });
    }
  }, [storeId, projectId, cacheProductsForStore]);

  // Mark a product out-of-stock (records qtyonhand=0). Marking OOS does NOT
  // clear an existing order qty — a rep may deliberately re-order an item that's
  // out of stock — so the row keeps its order/inventory fields AND shows the
  // OOS badge (see ProductCaptureRow).
  const addOOSItem = useCallback((product: CachedProduct) => {
    setCurrentItems((prev) => {
      const idx = prev.findIndex((i) => i.productid === product.productid);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantityOnHand: 0, isOOS: true, isNew: true };
        return updated;
      }
      return [
        ...prev,
        {
          productid: product.productid,
          upc: product.upc,
          description: product.description,
          orderqty: 0,
          cost: product.cost,
          packsize: product.packsize,
          order_increment: product.order_increment ?? 1,
          quantityOnHand: 0,
          isOOS: true,
          isNew: true,
        },
      ];
    });
    setTimeout(() => {
      setCurrentItems((prev) => prev.map((i) => ({ ...i, isNew: false })));
    }, 1000);
  }, []);

  // Handle a scan/typed code. Interpretation is decided by the active MODE, not
  // by guessing the symbology (the decoder only hands us digits):
  //   - oos mode     → match the value against a product CodeNbr → mark OOS.
  //   - capture mode → match a product UPC → open the qty/on-hand modal.
  const handleScan = useCallback(
    async (value: string) => {
      setScanError(null);

      if (mode === 'oos') {
        const oosProduct = await lookupByCodeNbr(value, storeId);
        if (oosProduct) {
          addOOSItem(oosProduct);
          if (navigator.vibrate) navigator.vibrate(60);
        } else {
          setScanError(`No product matches shelf tag "${value}"`);
          if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        }
        return;
      }

      // capture mode — product scan
      const product = await lookupByUPC(value, storeId);
      if (product) {
        setScannedProduct(product);
        // Start the order qty at one increment (a valid multiple). Reset on-hand.
        setQuantity(requiresOrder ? Math.max(1, product.order_increment ?? 1) : 0);
        setModalOnHand('');
        setShowQuantityModal(true);
        setIsScannerActive(false);
        return;
      }

      // Product not found — offer to add as unknown.
      setUnknownUPC(value);
      setUnknownDescription('');
      setUnknownCost('');
      setQuantity(1);
      setShowUnknownUpcModal(true);
      setIsScannerActive(false);
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    },
    [mode, lookupByUPC, lookupByCodeNbr, storeId, requiresOrder, addOOSItem]
  );

  // Handle manual UPC entry
  const handleManualSearch = useCallback(async () => {
    if (!manualUPC.trim()) return;
    await handleScan(manualUPC.trim());
    setManualUPC('');
    setShowManualEntry(false);
  }, [manualUPC, handleScan]);

  // Add scanned product to the capture list
  const addToOrder = useCallback(() => {
    if (!scannedProduct) return;
    const onHand =
      requiresInventory && modalOnHand !== '' ? Math.max(0, parseInt(modalOnHand, 10) || 0) : undefined;

    const newItem: CurrentOrderItem = {
      productid: scannedProduct.productid,
      upc: scannedProduct.upc,
      description: scannedProduct.description,
      orderqty: requiresOrder ? quantity : 0,
      cost: scannedProduct.cost,
      packsize: scannedProduct.packsize,
      order_increment: scannedProduct.order_increment ?? 1,
      quantityOnHand: onHand,
      isNew: true,
    };

    setCurrentItems((prev) => {
      const existingIndex = prev.findIndex((i) => i.productid === scannedProduct.productid);
      if (existingIndex >= 0) {
        // Update existing item: accumulate order qty (order projects) and adopt
        // the freshly-entered on-hand count if one was provided.
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          orderqty: requiresOrder ? updated[existingIndex].orderqty + quantity : updated[existingIndex].orderqty,
          quantityOnHand: onHand !== undefined ? onHand : updated[existingIndex].quantityOnHand,
          isNew: true,
        };
        return updated;
      }
      return [...prev, newItem];
    });

    // Clear and reset
    setScannedProduct(null);
    setQuantity(1);
    setModalOnHand('');
    setShowQuantityModal(false);
    setIsScannerActive(true);

    // Remove "new" flag after animation
    setTimeout(() => {
      setCurrentItems((prev) => prev.map((i) => ({ ...i, isNew: false })));
    }, 1000);
  }, [scannedProduct, quantity, requiresOrder, requiresInventory, modalOnHand]);

  // Discard the scanned product WITHOUT recording it and resume scanning (#2218).
  // The accidental-rescan escape hatch: shared by the modal's Cancel button and
  // the backdrop tap so an unwanted scan is never forced into the order.
  const cancelScan = useCallback(() => {
    setScannedProduct(null);
    setQuantity(1);
    setModalOnHand('');
    setShowQuantityModal(false);
    setIsScannerActive(true);
  }, []);

  // Add unknown UPC product to order
  const addUnknownToOrder = useCallback(() => {
    if (!unknownUPC) return;

    const cost = unknownCost ? parseFloat(unknownCost) : 0;
    const description = unknownDescription.trim() || `Unknown Product (${unknownUPC})`;

    // Use negative timestamp as temporary productid for unknown items
    const tempProductId = -Date.now();

    const newItem: CurrentOrderItem = {
      productid: tempProductId,
      upc: unknownUPC,
      description,
      orderqty: quantity,
      cost: isNaN(cost) ? 0 : cost,
      packsize: '',
      isNew: true,
      isUnknown: true,
    };

    setCurrentItems((prev) => {
      // Check if this UPC already exists (by UPC, not productid since unknown items have temp IDs)
      const existingIndex = prev.findIndex((i) => i.upc === unknownUPC && i.isUnknown);
      if (existingIndex >= 0) {
        // Update existing unknown item
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          orderqty: updated[existingIndex].orderqty + quantity,
          isNew: true,
        };
        return updated;
      }
      return [...prev, newItem];
    });

    // Clear and reset
    setUnknownUPC('');
    setUnknownDescription('');
    setUnknownCost('');
    setQuantity(1);
    setShowUnknownUpcModal(false);
    setIsScannerActive(true);

    // Remove "new" flag after animation
    setTimeout(() => {
      setCurrentItems((prev) => prev.map((i) => ({ ...i, isNew: false })));
    }, 1000);
  }, [unknownUPC, unknownDescription, unknownCost, quantity]);

  // Cancel unknown UPC modal
  const cancelUnknownUpc = useCallback(() => {
    setShowUnknownUpcModal(false);
    setUnknownUPC('');
    setUnknownDescription('');
    setUnknownCost('');
    setQuantity(1);
    setIsScannerActive(true);
  }, []);

  // Remove item from order
  const removeItem = useCallback((productId: number) => {
    setCurrentItems((prev) => prev.filter((i) => i.productid !== productId));
  }, []);

  // Update item order quantity — steps in the product's order_increment (#2022).
  const updateItemQty = useCallback((productId: number, direction: 1 | -1) => {
    setCurrentItems((prev) =>
      prev.map((item) =>
        item.productid === productId
          ? { ...item, orderqty: stepQty(item.orderqty, direction, item.order_increment) }
          : item
      )
    );
  }, []);

  // Update item qty-on-hand (#2022 inventory capture).
  const updateItemOnHand = useCallback((productId: number, value: number | undefined) => {
    setCurrentItems((prev) =>
      prev.map((item) => (item.productid === productId ? { ...item, quantityOnHand: value } : item))
    );
  }, []);

  // Save order — routes through the offline-first sync queue (#2013). The op
  // does applyLocal + enqueue; the sync engine drains it automatically and
  // resolves the server callid from `visitId` when the check-in hasn't synced.
  const handleSaveOrder = useCallback(async () => {
    if (currentItems.length === 0) return;
    // Hard gate: never POST an order the server would 400 (#2022). Inventory-only
    // submissions skip the order rules (validation.ok is always true there).
    if (requiresOrder && !validation.ok) return;

    // Drop fully-empty lines so a row stepped down to 0 with nothing else
    // captured never ships an orphan orderqty=0 line. A line is real if it has
    // an order qty, an on-hand count, or an OOS flag.
    const lines = currentItems.filter(
      (item) => item.orderqty > 0 || item.quantityOnHand !== undefined || item.isOOS,
    );
    if (lines.length === 0) {
      setScanError('Nothing to save yet.');
      return;
    }

    setIsSaving(true);
    try {
      await writeOps.createOrder.apply({
        visitLocalId: visitId,
        callId,
        storeId,
        projectId,
        associateId,
        products: lines.map((item) => ({
          productid: item.productid,
          // Inventory-only lines carry no order quantity.
          orderqty: requiresOrder ? item.orderqty : 0,
          ...(item.quantityOnHand !== undefined ? { quantity_on_hand: item.quantityOnHand } : {}),
          // Client-only flag so createOrder emits a distinct oos_recorded event.
          ...(item.isOOS ? { is_oos: true } : {}),
          // #2340 — carry the description so the checkout review shows the real
          // name even if the product cache is empty at checkout. Stripped from the POST.
          ...(item.description ? { description: item.description } : {}),
        })),
      });

      // Clear current capture
      setCurrentItems([]);

      // Auto-return to the call after completion (#2341): show a brief confirmation,
      // then navigate back to where they came from (the call/StoreDetail) so the rep
      // doesn't have to press Back. navigate(-1) preserves StoreDetail's state.
      setJustSaved(true);
      setTimeout(() => navigate(-1), 750);
    } catch (err) {
      console.error('Error saving order:', err);
      alert('Could not save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [currentItems, requiresOrder, validation.ok, visitId, callId, storeId, projectId, associateId, navigate]);

  // Product list for browsing
  const [allProducts, setAllProducts] = useState<CachedProduct[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  useEffect(() => {
    async function loadProducts() {
      if (!showProductList || !storeId) return;

      setIsLoadingProducts(true);
      try {
        // First try local cache
        const cachedProducts = await getProductsForStore(storeId);
        if (cachedProducts.length > 0) {
          setAllProducts(cachedProducts);
          return;
        }

        // Cache is empty - fetch all products from API (no distributor filter)
        // via the authenticated client. (Was a raw fetch reading a non-existent
        // `rdm_token` localStorage key -> always 401; the app stores the JWT as
        // `accessToken`, which apiFetch/getAuthToken use.)
        const data = await apiFetch<{ results?: unknown[]; data?: unknown[] }>(
          '/api/distributors/products/?limit=500'
        );
        // Handle both paginated (results array) and non-paginated (direct array) responses
        const productList = data?.results || data?.data || (Array.isArray(data) ? data : []);
        if (Array.isArray(productList)) {
          // Convert API response to CachedProduct format
          const products: CachedProduct[] = (productList as Array<Record<string, unknown>>).map((p) => ({
            productid: (p.productid as number) || 0,
            upc: (p.upc as string) || '',
            description: (p.description as string) || '',
            codenbr: (p.codenbr as string) || '',
            cost: typeof p.cost === 'string' ? parseFloat(p.cost) : (p.cost as number) || 0,
            packsize: (p.packsize as string) || '',
            size: (p.size as string) || '',
            uom: (p.uom as string) || '',
            brand: (p.brand as string) || '',
            order_increment: (p.order_increment as number) ?? 1,
            projectId: 0,
            storeId: storeId,
            distributorId: (p.distributor as { id?: number })?.id || 0,
            cachedAt: new Date().toISOString(),
          }));
          setAllProducts(products);
        }
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setIsLoadingProducts(false);
      }
    }

    loadProducts();
  }, [showProductList, storeId, getProductsForStore]);

  const filteredProducts = useMemo(() => {
    if (!productSearch) return allProducts.slice(0, 50);
    const search = productSearch.toLowerCase();
    return allProducts
      .filter(
        (p) =>
          p.description.toLowerCase().includes(search) ||
          p.upc.includes(search) ||
          p.codenbr.toLowerCase().includes(search)
      )
      .slice(0, 50);
  }, [allProducts, productSearch]);

  // Validate we have required params (check for 0 explicitly since parseInt returns 0 for invalid strings)
  const hasValidStoreId = storeId > 0;
  const hasValidProjectId = projectId > 0;

  if (!hasValidStoreId || !hasValidProjectId) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <h2>Invalid Store Context</h2>
        <p>
          {!hasValidStoreId
            ? 'No store selected. '
            : !hasValidProjectId
              ? 'No project available for this store. '
              : ''}
          Please go back and select a store with an active project.
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: 20,
            padding: '12px 24px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            marginRight: 12,
          }}
        >
          Go Back
        </button>
        <button
          onClick={() => navigate('/today')}
          style={{
            marginTop: 20,
            padding: '12px 24px',
            backgroundColor: '#64748b',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
          }}
        >
          Home
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: 'white',
        paddingBottom: 100,
      }}
    >
      {/* #2341 post-save confirmation overlay — shows briefly, then auto-returns to the call */}
      {justSaved && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            backgroundColor: 'rgba(15,23,42,0.92)',
          }}
        >
          <div style={{ fontSize: 48 }}>✓</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {requiresOrder ? 'Order saved' : 'Saved'}
          </div>
          <div style={{ fontSize: 14, color: '#94a3b8' }}>Returning to your call…</div>
        </div>
      )}
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          backgroundColor: '#1e293b',
          borderBottom: '1px solid #334155',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: 24,
            cursor: 'pointer',
            padding: 4,
          }}
        >
          &larr;
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 18, margin: 0 }}>
            {mode === 'oos'
              ? 'Out of Stock'
              : requestedMode === 'inventory'
                ? 'Inventory'
                : requestedMode === 'order'
                  ? 'Order Entry'
                  : requiresOrder
                    ? 'Order Entry'
                    : inventoryOnly
                      ? 'Inventory'
                      : 'Capture'}
          </h1>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>{storeName}</p>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12, color: '#94a3b8' }}>
          {cachedCount} products cached
        </div>
      </div>

      {/* Scanner Section */}
      <div style={{ padding: 20 }}>
        {/* #2188 (diag #54) — project requires an order but has no distributor. */}
        {orderingConfigError && (
          <div
            data-testid="ordering-config-error"
            style={{
              backgroundColor: '#78350f',
              color: '#fde68a',
              padding: '14px 16px',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 14,
              lineHeight: 1.4,
            }}
          >
            <strong style={{ display: 'block', marginBottom: 4 }}>Ordering not configured</strong>
            {orderingConfigError}
          </div>
        )}
        {/* #2022 Phase 4 — Products / Out-of-Stock mode toggle (combined projects). */}
        {requiresOos && (requiresOrder || requiresInventory) && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }} data-testid="capture-mode-toggle">
            <button
              onClick={() => {
                setMode('capture');
                setScanError(null);
                setIsScannerActive(true);
              }}
              data-testid="mode-products"
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                backgroundColor: mode === 'capture' ? '#2563eb' : '#334155',
                color: 'white',
              }}
            >
              Products
            </button>
            <button
              onClick={() => {
                setMode('oos');
                setScanError(null);
                setIsScannerActive(true);
              }}
              data-testid="mode-oos"
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                backgroundColor: mode === 'oos' ? '#dc2626' : '#334155',
                color: 'white',
              }}
            >
              Out of Stock
            </button>
          </div>
        )}
        {requiresOos && (
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
            {mode === 'oos'
              ? 'Scan each shelf tag where the product is OUT OF STOCK.'
              : 'Scan product barcodes to add to the order/inventory.'}
          </p>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <BarcodeScanner
            onScan={handleScan}
            onError={(err) => setScanError(err)}
            isActive={isScannerActive && !showQuantityModal && !showManualEntry && !showProductList}
            width={Math.min(window.innerWidth - 40, 350)}
            height={280}
          />
        </div>

        {/* Scan error */}
        {scanError && (
          <div
            style={{
              backgroundColor: '#7f1d1d',
              color: '#fecaca',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>{scanError}</span>
            <button
              onClick={() => setScanError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#fecaca',
                fontSize: 18,
                cursor: 'pointer',
              }}
            >
              &times;
            </button>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <button
            onClick={() => setShowManualEntry(true)}
            style={{
              flex: 1,
              padding: '12px 16px',
              backgroundColor: '#334155',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Manual Entry
          </button>
          {/* #2188 — when ordering isn't configured there is no project catalog,
              so Browse Products would only show the GENERIC catalog and let the
              rep build an order against products that aren't this project's
              (the diag #54 → wrongly-orphaned-order tangle). Hide it. */}
          {!orderingConfigError && (
            <button
              onClick={() => setShowProductList(true)}
              style={{
                flex: 1,
                padding: '12px 16px',
                backgroundColor: '#334155',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Browse Products
            </button>
          )}
        </div>
      </div>

      {/* Current Order */}
      <div style={{ padding: '0 20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 16, margin: 0 }}>
            {requiresOrder
              ? `Current Order (${totalItems} items)`
              : `Captured (${currentItems.length} items)`}
          </h2>
          {requiresOrder && (
            <span style={{ fontSize: 18, fontWeight: 600, color: '#10b981' }}>
              ${totalCost.toFixed(2)}
            </span>
          )}
        </div>

        {currentItems.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#64748b',
            }}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ margin: '0 auto 12px' }}
            >
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            <p>{mode === 'oos' ? 'Scan a shelf tag to mark out of stock' : 'Scan a barcode to add items'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {currentItems.map((item) => (
              <ProductCaptureRow
                key={item.productid}
                item={item}
                requiresOrder={requiresOrder}
                requiresInventory={requiresInventory}
                overMax={overMaxIds.has(item.productid)}
                onChangeQty={updateItemQty}
                onChangeOnHand={updateItemOnHand}
                onRemove={removeItem}
              />
            ))}
          </div>
        )}
      </div>

      {/* Save Order Button */}
      {currentItems.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 20,
            backgroundColor: '#1e293b',
            borderTop: '1px solid #334155',
          }}
        >
          {requiresOrder && !validation.ok && validation.reasons.length > 0 && (
            <p
              data-testid="save-block-reason"
              style={{ margin: '0 0 10px', fontSize: 13, color: '#fca5a5', textAlign: 'center' }}
            >
              {validation.reasons.join(' ')}
            </p>
          )}
          <button
            onClick={handleSaveOrder}
            disabled={isSaving || (requiresOrder && !validation.ok)}
            data-testid="save-capture-button"
            style={{
              width: '100%',
              padding: '16px 24px',
              backgroundColor: isSaving || (requiresOrder && !validation.ok) ? '#334155' : '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 600,
              cursor: isSaving || (requiresOrder && !validation.ok) ? 'not-allowed' : 'pointer',
            }}
          >
            {isSaving ? 'Saving...' : requiresOrder ? `Save Order - $${totalCost.toFixed(2)}` : 'Save'}
          </button>
        </div>
      )}

      {/* Quantity Modal */}
      {showQuantityModal && scannedProduct && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={cancelScan}
        >
          <div
            style={{
              backgroundColor: '#1e293b',
              borderRadius: '20px 20px 0 0',
              padding: 24,
              width: '100%',
              maxWidth: 400,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{scannedProduct.description}</h3>
            <p style={{ margin: '0 0 20px', color: '#94a3b8', fontSize: 14 }}>
              {scannedProduct.upc} | Pack: {scannedProduct.packsize} | ${scannedProduct.cost.toFixed(2)}
            </p>

            {requiresOrder && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 24,
                  marginBottom: 12,
                }}
              >
                <button
                  onClick={() => setQuantity((q) => stepQty(q, -1, scannedProduct.order_increment))}
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: '#334155',
                    color: 'white',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                  }}
                >
                  -
                </button>
                <span
                  style={{ fontSize: 48, fontWeight: 700, minWidth: 80, textAlign: 'center' }}
                  data-testid="modal-qty"
                >
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity((q) => stepQty(q, 1, scannedProduct.order_increment))}
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: '#334155',
                    color: 'white',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                  }}
                >
                  +
                </button>
              </div>
            )}
            {requiresOrder && (scannedProduct.order_increment ?? 1) > 1 && (
              <p style={{ margin: '0 0 16px', textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
                Orders in multiples of {scannedProduct.order_increment}
              </p>
            )}

            {requiresInventory && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#94a3b8' }}>
                  Quantity on hand
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={modalOnHand}
                  onChange={(e) => setModalOnHand(e.target.value)}
                  placeholder="0"
                  data-testid="modal-on-hand"
                  style={{
                    width: '100%',
                    padding: 12,
                    backgroundColor: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 16,
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}

            {(() => {
              // Require a usable value before adding: a positive order qty for
              // order projects, AND an entered on-hand count for inventory
              // projects (so an inventory line is never silently dropped).
              const canAdd = (!requiresOrder || quantity > 0) && (!requiresInventory || modalOnHand !== '');
              return (
                <button
                  onClick={addToOrder}
                  disabled={!canAdd}
                  data-testid="modal-add-button"
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    backgroundColor: canAdd ? '#10b981' : '#334155',
                    color: 'white',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: canAdd ? 'pointer' : 'not-allowed',
                  }}
                >
                  {requiresOrder ? `Add to Order - $${(scannedProduct.cost * quantity).toFixed(2)}` : 'Add'}
                </button>
              );
            })()}

            {/* #2218 — explicit escape: discard this scan and resume scanning. */}
            <button
              onClick={cancelScan}
              data-testid="modal-cancel-button"
              style={{
                width: '100%',
                marginTop: 12,
                padding: '14px 24px',
                backgroundColor: 'transparent',
                color: '#94a3b8',
                border: '1px solid #334155',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel &amp; re-scan
            </button>
          </div>
        </div>
      )}

      {/* Unknown UPC Modal */}
      {showUnknownUpcModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={cancelUnknownUpc}
        >
          <div
            style={{
              backgroundColor: '#1e293b',
              borderRadius: '20px 20px 0 0',
              padding: 24,
              width: '100%',
              maxWidth: 400,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Warning Banner */}
            <div
              style={{
                backgroundColor: '#78350f',
                color: '#fef3c7',
                padding: '12px 16px',
                borderRadius: 8,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span style={{ fontSize: 14 }}>Product not found in catalog</span>
            </div>

            <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Add Unknown Product</h3>
            <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: 14, fontFamily: 'monospace' }}>
              UPC: {unknownUPC}
            </p>

            {/* Description Input */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#94a3b8' }}>
                Description (optional)
              </label>
              <input
                type="text"
                value={unknownDescription}
                onChange={(e) => setUnknownDescription(e.target.value)}
                placeholder="Enter product description..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0f172a',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Cost Input */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#94a3b8' }}>
                Cost (optional)
              </label>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={unknownCost}
                onChange={(e) => setUnknownCost(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: '#0f172a',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Quantity Selector */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 24,
                marginBottom: 20,
              }}
            >
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#334155',
                  color: 'white',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                }}
              >
                -
              </button>
              <span style={{ fontSize: 36, fontWeight: 700, minWidth: 60, textAlign: 'center' }}>
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#334155',
                  color: 'white',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                }}
              >
                +
              </button>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={cancelUnknownUpc}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  backgroundColor: '#334155',
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={addUnknownToOrder}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  backgroundColor: '#d97706',
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualEntry && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: 20,
          }}
          onClick={() => setShowManualEntry(false)}
        >
          <div
            style={{
              backgroundColor: '#1e293b',
              borderRadius: 16,
              padding: 24,
              width: '100%',
              maxWidth: 350,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Enter UPC Code</h3>
            <input
              type="text"
              value={manualUPC}
              onChange={(e) => setManualUPC(e.target.value)}
              placeholder="Enter UPC..."
              autoFocus
              style={{
                width: '100%',
                padding: 16,
                fontSize: 18,
                backgroundColor: '#0f172a',
                border: '1px solid #334155',
                borderRadius: 8,
                color: 'white',
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
            />
            <button
              onClick={handleManualSearch}
              style={{
                width: '100%',
                padding: '14px 24px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Search
            </button>
          </div>
        </div>
      )}

      {/* Product List Modal */}
      {showProductList && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: '#0f172a',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '16px 20px',
              backgroundColor: '#1e293b',
              borderBottom: '1px solid #334155',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <button
              onClick={() => setShowProductList(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: 24,
                cursor: 'pointer',
              }}
            >
              &larr;
            </button>
            <input
              type="text"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Search products..."
              style={{
                flex: 1,
                padding: '10px 16px',
                fontSize: 14,
                backgroundColor: '#0f172a',
                border: '1px solid #334155',
                borderRadius: 8,
                color: 'white',
              }}
            />
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
            {isLoadingProducts && (
              <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    border: '3px solid #334155',
                    borderTopColor: '#3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 12px',
                  }}
                />
                Loading products...
              </div>
            )}
            {!isLoadingProducts && filteredProducts.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                <p style={{ fontSize: 14 }}>No products found</p>
                <p style={{ fontSize: 12, marginTop: 8 }}>
                  {productSearch ? 'Try a different search term' : 'Products will be available after syncing'}
                </p>
              </div>
            )}
            {!isLoadingProducts && filteredProducts.map((product) => (
              <div
                key={product.productid}
                onClick={() => {
                  setShowProductList(false);
                  if (mode === 'oos') {
                    // In OOS mode, picking a product marks it out-of-stock.
                    addOOSItem(product);
                    return;
                  }
                  setScannedProduct(product);
                  setQuantity(requiresOrder ? Math.max(1, product.order_increment ?? 1) : 0);
                  setModalOnHand('');
                  setShowQuantityModal(true);
                }}
                style={{
                  backgroundColor: '#1e293b',
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 8,
                  cursor: 'pointer',
                }}
              >
                <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>{product.description}</p>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8' }}>
                  {product.upc} | {product.brand} | ${product.cost.toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderEntry;
